import {isFilled} from '@prismicio/client'
import {PrismicNextImage} from '@prismicio/next/pages'
import cn from 'classnames'
import dayjs from 'dayjs'
import Link from 'next/link'
import {useLinkResolver} from 'prismicio'
import type {EventDocument} from 'prismicio-types'
import type {FC} from 'react'
import {Fragment} from 'react'
import {useGetEventStatus} from 'src/common/hooks/useGetEventStatus'
import {Icon} from 'src/common/Icon'
import type {useEventLabels} from '../hooks/useEventLabels'
import {Tag} from './Hero/components/Tag'

type EventCardProps = {
  event: EventDocument<string>
  labels: ReturnType<typeof useEventLabels>['cards']
  isActive?: boolean
}

export const EventCard: FC<EventCardProps> = ({event, labels, isActive = true}) => {
  const {linkResolver} = useLinkResolver()

  const hasData = 'data' in event && event.data
  const data = hasData ? event.data : undefined

  const eventStatus = useGetEventStatus({
    eventDate: data?.event_date,
    eventFinishDate: data?.event_finish_date,
    eventType: data?.event_type,
  })

  if (!hasData || !data) {
    return null
  }

  const isOnDemand = isFilled.select(data.event_type) && data.event_type === 'On demand'

  const isPastEvent = eventStatus?.label === 'PAST EVENT'
  const shouldShowTag = !isOnDemand && eventStatus

  const date = isFilled.timestamp(data.event_date) ? dayjs(data.event_date).format('MMMM DD, YYYY') : null
  const time = isFilled.timestamp(data.event_date) ? dayjs(data.event_date).format('h A') : null

  // Build array of metadata items to display
  const metadataItems: React.ReactNode[] = []

  // Add date
  if (date) {
    metadataItems.push(
      <p className="text-10 font-semibold uppercase leading-normal tracking-[0.6px] text-moss-grey-400">{date}</p>,
    )
  }

  // Add time (only for non-past online events)
  if (!isPastEvent && time && isFilled.select(data.event_type) && data.event_type === 'Online') {
    metadataItems.push(
      <p className="text-10 font-semibold uppercase leading-normal tracking-[0.6px] text-moss-grey-400">{time}</p>,
    )
  }

  // Add location (only for non-past in-person events)
  if (
    !isPastEvent &&
    isFilled.keyText(data.location) &&
    isFilled.select(data.event_type) &&
    data.event_type === 'In person'
  ) {
    metadataItems.push(
      <p className="text-10 font-semibold uppercase leading-normal tracking-[0.6px] text-moss-grey-400">
        {data.location}
      </p>,
    )
  }

  if (isFilled.keyText(data.language)) {
    metadataItems.push(
      <p className="text-10 font-semibold uppercase leading-normal tracking-[0.6px] text-moss-grey-400">
        {data.language}
      </p>,
    )
  }

  return (
    <Link href={linkResolver(event)}>
      <div
        className={cn(
          'group flex h-[495px] w-full max-w-[384px] flex-col gap-6 rounded-[8px] border border-black/10 bg-moss-white p-6 transition-opacity duration-300 hover:shadow-md md:h-[500px] ',
          {
            'opacity-30': !isActive,
            'opacity-100': isActive,
          },
        )}
      >
        {isFilled.image(data.hero_image) && (
          <div className="aspect-[287/165] w-full overflow-hidden rounded-[4px]">
            <PrismicNextImage
              field={data.hero_image}
              className="size-full rounded-[4px] object-cover transition-transform duration-300 group-hover:scale-105"
            />
          </div>
        )}

        <div className="flex flex-1 flex-col gap-4">
          <div className="flex flex-wrap items-center gap-4">
            {shouldShowTag && eventStatus && <Tag variant={eventStatus.variant}>{eventStatus.label}</Tag>}
            <div className="flex items-center gap-2">
              {metadataItems.map((item, index) => (
                <Fragment key={index}>
                  {item}
                  {index < metadataItems.length - 1 && <div className="size-1 rounded-[1px] bg-moss-grey-200" />}
                </Fragment>
              ))}
            </div>
          </div>

          <div className="flex flex-1 flex-col gap-2">
            {isFilled.keyText(data.title) && (
              <h3 className="line-clamp-2 text-18 font-medium leading-[24px] text-moss-black">{data.title}</h3>
            )}
            {isFilled.keyText(data.meta_description) && (
              <p className="line-clamp-4 text-15 leading-5 text-moss-grey-550">{data.meta_description}</p>
            )}
          </div>
        </div>

        <div
          // href={linkResolver(event)}
          className="group relative flex h-[38px] w-fit items-center justify-center gap-[14px] rounded-[4px] bg-moss-white pl-[14px] pr-[7px] hover:bg-moss-grey-100"
        >
          {isOnDemand ? (
            <>
              <span className="text-10 font-semibold uppercase leading-normal tracking-[0.6px] text-moss-black">
                {isFilled.keyText(labels.watchButton) && labels.watchButton}
              </span>
              <div className="flex size-6 items-center justify-center rounded-[2px] bg-green-neon group-hover:bg-green-neon/[80%]">
                <Icon name="play" className="size-3 text-green-dark" />
              </div>
            </>
          ) : (
            <>
              <span className="text-10 font-semibold uppercase leading-normal tracking-[0.6px] text-moss-black">
                {isFilled.keyText(data.registerButton)
                  ? data.registerButton
                  : isFilled.keyText(labels.registerButton) && labels.registerButton}
              </span>
              <div className="flex size-6 items-center justify-center rounded-[2px] bg-green-neon group-hover:bg-green-neon/[80%]">
                <Icon name="arrow" className="h-2 w-2.5 text-green-dark" />
              </div>
            </>
          )}
          <div className="pointer-events-none absolute inset-0 rounded-[4px] border border-moss-black/[10%]" />
        </div>
      </div>
    </Link>
  )
}
